/**
 * Admin Audit Log panel (storyboard Flow J; Stream G G2).
 *
 * The dockable surface that lists administrative + security-relevant
 * audit events with filtering, inline row-expansion, and CSV/JSONL
 * export. **Admin-only at MVP** (the 2026-06-08 §6 ruling): the panel is
 * filtered out of the add-panel menu for non-admins (REQ-UI-047) and the
 * BFF gates the endpoint with ``require_roles("admin")``; this component
 * additionally renders a permission-denied state as defence-in-depth if
 * it is ever mounted without the role.
 *
 * Data: :func:`useAuditEvents` (TanStack Query over
 * ``GET /api/admin/audit/events``). Reading the log is NOT itself audited
 * (Flow J / ADR-006) — only the export is, server-side. Pagination walks
 * the BFF's opaque cursor (Previous/Next); changing any filter resets it.
 *
 * Cross-references:
 *   - REQ-UI-022 (Audit Log surface); REQ-LOG-019..022 (audit query).
 *   - REQ-AUTH-022 (admin RBAC); REQ-UI-059 (keyboard nav + table
 *     semantics; sortable headers carry ``aria-sort`` + Enter/Space).
 *   - Flow J (docs/engineering/detail/storyboards/flows/J-audit-log);
 *     ADR-024 §2 (dockable surface inventory).
 */
import { useCallback, useMemo, useState } from "react";
import { CaretDown, CaretRight, CaretUp } from "@phosphor-icons/react";
import { AuditFilterBar } from "@/audit/audit-filter-bar";
import { AuditColumnSettingsMenu } from "@/audit/audit-column-settings-menu";
import { AuditExportModal } from "@/audit/audit-export-modal";
import {
  AUDIT_COLUMNS,
  type AuditColumnDef,
  type AuditSortBy,
  type AuditSortDir,
  DEFAULT_AUDIT_FILTER,
  DEFAULT_AUDIT_SORT_BY,
  DEFAULT_AUDIT_SORT_DIR,
  type AuditFilterState,
  deriveResource,
  deriveSourceIp,
  formatAuditTime,
  loadVisibleColumnKeys,
  nextSort,
  outcomeMeta,
  presetStartIso,
  saveVisibleColumnKeys,
  toggleColumnKey,
} from "@/audit/audit-display";
import {
  AUDIT_LOG_READER_ROLES,
  type AuditEventItem,
  type AuditEventsFilter,
  useAuditEvents,
} from "@/audit/use-audit-events";
import { useSession } from "@/auth/session-context";
import { cn } from "@/lib/utils";

/**
 * Render the admin Audit Log panel.
 *
 * Mounted by :file:`@/multi-pane/panel-registry.tsx` against the
 * ``audit-log`` :type:`PanelId` (admin-gated in the registry). Full-
 * height column: header + filter bar + scrollable table + pagination.
 */
export function AuditLogPanel(): React.ReactElement {
  const session = useSession();
  const isAdmin = session.hasAnyRole(AUDIT_LOG_READER_ROLES);

  const [filterState, setFilterStateRaw] = useState<AuditFilterState>(DEFAULT_AUDIT_FILTER);
  const [cursor, setCursor] = useState<string | null>(null);
  const [prevCursors, setPrevCursors] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  // Server-side sort (Flow J default: Time↓). One state object so the
  // pure :func:`nextSort` reducer drives a single setter (no nested-
  // setState dance). Re-sent on every page so the order holds across
  // Previous / Next.
  const [sort, setSort] = useState<{ by: AuditSortBy; dir: AuditSortDir }>({
    by: DEFAULT_AUDIT_SORT_BY,
    dir: DEFAULT_AUDIT_SORT_DIR,
  });
  // Column visibility — seeded from localStorage (Flow J defaults when
  // absent), persisted on each toggle. Min-1 invariant enforced in
  // :func:`toggleColumnKey`.
  const [visibleCols, setVisibleCols] = useState<Set<string>>(loadVisibleColumnKeys);

  // Any filter change resets pagination + collapses the open row. Sort is
  // intentionally preserved across filter changes (the operator's chosen
  // ordering shouldn't reset just because they narrowed the result set).
  const setFilterState = (next: AuditFilterState): void => {
    setFilterStateRaw(next);
    setCursor(null);
    setPrevCursors([]);
    setExpandedId(null);
  };

  // Activate a sortable header: flip direction on the active column,
  // else select the clicked column ascending (Appaloosa handleSort, via
  // the pure :func:`nextSort` reducer). Sort changes reset pagination
  // (the cursor is order-dependent) and collapse the open row — a re-sort
  // invalidates the prior page window.
  const handleSort = useCallback((clicked: AuditSortBy): void => {
    setSort((current) => nextSort(current, clicked));
    setCursor(null);
    setPrevCursors([]);
    setExpandedId(null);
  }, []);

  // Toggle a column's visibility (min-1 enforced) + persist to localStorage.
  const handleToggleColumn = useCallback((key: string): void => {
    setVisibleCols((current) => {
      const updated = toggleColumnKey(current, key);
      saveVisibleColumnKeys(updated);
      return updated;
    });
  }, []);

  // The rendered columns, in the canonical left-to-right order.
  const activeColumns = useMemo(
    () => AUDIT_COLUMNS.filter((col) => visibleCols.has(col.key)),
    [visibleCols],
  );

  // The window's "now" is snapshotted when the range selection changes
  // (not on every render) so the query key stays stable and the page
  // doesn't refetch on unrelated re-renders. Re-selecting the same range
  // is the explicit "refresh the window" gesture.
  const startIso = useMemo(
    // Snapshot now() when the range selection changes (not on every
    // render) so the query key stays stable; re-selecting the range is
    // the explicit "refresh the window" gesture.
    () => presetStartIso(filterState.timeRangeId, Date.now()),
    [filterState.timeRangeId],
  );

  const wireFilter: AuditEventsFilter = useMemo(
    () => ({
      start: startIso,
      actor: filterState.actor.trim() || null,
      actionKind: filterState.actionKind,
      outcome: filterState.outcome,
      q: filterState.q.trim() || null,
      includeConnectorAuth: filterState.includeConnectorAuth,
      sortBy: sort.by,
      sortDir: sort.dir,
      cursor,
    }),
    [
      startIso,
      filterState.actor,
      filterState.actionKind,
      filterState.outcome,
      filterState.q,
      filterState.includeConnectorAuth,
      sort.by,
      sort.dir,
      cursor,
    ],
  );

  const query = useAuditEvents(wireFilter);

  // Defence-in-depth: the registry already hides the panel from non-
  // admins (REQ-UI-047) + the BFF returns 403; this is the belt-and-
  // braces in-panel guard (Flow O semantics, inline).
  if (!isAdmin) {
    return <ForbiddenState />;
  }

  const page = query.data;
  const items = page?.items ?? [];

  const goNext = (): void => {
    if (page?.next_cursor !== undefined && page.next_cursor !== null) {
      setPrevCursors((stack) => [...stack, cursor ?? ""]);
      setCursor(page.next_cursor);
      setExpandedId(null);
    }
  };

  const goPrev = (): void => {
    if (prevCursors.length === 0) return;
    const stack = [...prevCursors];
    const prev = stack.pop() ?? "";
    setPrevCursors(stack);
    setCursor(prev === "" ? null : prev);
    setExpandedId(null);
  };

  const countLabel = page ? `${page.total} ${page.total === 1 ? "record" : "records"}` : "";

  return (
    <div
      data-testid="audit-log-panel"
      role="region"
      aria-label="Audit log"
      className="flex h-full w-full flex-col bg-bg-base"
    >
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-body font-medium text-text-primary">
          Audit log{" "}
          <span className="ml-2 text-body-sm text-text-muted" data-testid="audit-log-count">
            {countLabel}
          </span>
        </h2>
        <AuditColumnSettingsMenu
          visibleCols={visibleCols}
          onToggleColumn={handleToggleColumn}
          onExport={() => setExportOpen(true)}
        />
      </header>

      <AuditFilterBar value={filterState} onChange={setFilterState} />

      <div className="flex-1 overflow-auto">
        {query.isLoading ? (
          <LoadingState />
        ) : query.isError ? (
          <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <table className="w-full border-collapse text-body-sm" data-testid="audit-log-table">
            <thead className="sticky top-0 z-10 bg-bg-surface">
              <tr className="border-b border-border text-left text-meta uppercase tracking-wide text-text-secondary">
                <th scope="col" className="w-8 px-2 py-2">
                  <span className="sr-only">Expand row</span>
                </th>
                {activeColumns.map((col) => (
                  <AuditHeaderCell
                    key={col.key}
                    column={col}
                    sortBy={sort.by}
                    sortDir={sort.dir}
                    onSort={handleSort}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <AuditRow
                  key={item.id}
                  item={item}
                  columns={activeColumns}
                  expanded={expandedId === item.id}
                  onToggle={() =>
                    setExpandedId((current) => (current === item.id ? null : item.id))
                  }
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {page !== undefined && items.length > 0 && (
        <footer className="flex items-center justify-between border-t border-border px-3 py-2 text-body-sm text-text-secondary">
          <span data-testid="audit-log-page-info">
            Showing {items.length} of {page.total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={goPrev}
              disabled={prevCursors.length === 0}
              className="rounded border border-border px-2 py-1 text-text-primary hover:bg-bg-elevated disabled:opacity-40"
              data-testid="audit-log-prev"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={page.next_cursor === undefined || page.next_cursor === null}
              className="rounded border border-border px-2 py-1 text-text-primary hover:bg-bg-elevated disabled:opacity-40"
              data-testid="audit-log-next"
            >
              Next
            </button>
          </div>
        </footer>
      )}

      <AuditExportModal
        open={exportOpen}
        onOpenChange={setExportOpen}
        filter={wireFilter}
        total={page?.total ?? 0}
      />
    </div>
  );
}

/**
 * Map of how each column renders its ``<td>`` body from an audit item.
 *
 * Keyed by :type:`AuditColumnDef`'s ``key``. Kept here (in the panel,
 * not the DOM-free ``audit-display`` module) because the cells are JSX.
 * ``outcome`` returns a node so the row can attach its
 * ``audit-row-outcome-*`` test id + semantic-token color from
 * :func:`outcomeMeta`.
 */
const CELL_RENDERERS: Record<string, (item: AuditEventItem) => React.ReactNode> = {
  time: (item) => (
    <span className="font-mono tabular-nums text-text-secondary">{formatAuditTime(item.time)}</span>
  ),
  actor: (item) => <span className="text-text-primary">{item.actor ?? "—"}</span>,
  event_type: (item) => <span className="font-mono text-text-secondary">{item.event_type}</span>,
  resource: (item) => (
    <span className="font-mono text-text-secondary">{deriveResource(item.details)}</span>
  ),
  outcome: (item) => {
    const meta = outcomeMeta(item.outcome);
    return (
      <span
        className={cn("font-medium", meta.className)}
        data-testid={`audit-row-outcome-${item.id}`}
      >
        {meta.label}
      </span>
    );
  },
  source_ip: (item) => (
    <span className="font-mono text-text-secondary">{deriveSourceIp(item.details)}</span>
  ),
  service: (item) => <span className="text-text-secondary">{item.service}</span>,
  actor_kind: (item) => (
    <span className="font-mono text-text-secondary">{item.actor_kind ?? "—"}</span>
  ),
  correlation_id: (item) => (
    <span className="font-mono text-text-secondary">{item.correlation_id ?? "—"}</span>
  ),
};

interface AuditHeaderCellProps {
  column: AuditColumnDef;
  sortBy: AuditSortBy;
  sortDir: AuditSortDir;
  onSort: (by: AuditSortBy) => void;
}

/**
 * One column header. Sortable columns (``column.sortKey``) render a
 * clickable / Enter-Space-activatable ``<th>`` carrying ``aria-sort``
 * (``ascending`` / ``descending`` when active, ``none`` otherwise) + a
 * chevron indicator; derived columns render a plain label (no affordance,
 * no ``aria-sort`` — they have no BFF ``sort_by``). REQ-UI-059.
 */
function AuditHeaderCell({
  column,
  sortBy,
  sortDir,
  onSort,
}: AuditHeaderCellProps): React.ReactElement {
  const sortKey = column.sortKey;
  if (sortKey === undefined) {
    return (
      <th scope="col" className="px-3 py-2" data-testid={`audit-header-${column.key}`}>
        {column.label}
      </th>
    );
  }
  const active = sortBy === sortKey;
  const ariaSort: "ascending" | "descending" | "none" = active
    ? sortDir === "asc"
      ? "ascending"
      : "descending"
    : "none";
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className="px-3 py-2"
      data-testid={`audit-header-${column.key}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`audit-sort-${column.key}`}
        className="group flex items-center gap-1 uppercase tracking-wide text-text-secondary hover:text-text-primary focus:outline-none focus-visible:text-text-primary"
      >
        <span>{column.label}</span>
        <SortIndicator active={active} dir={active ? sortDir : null} />
      </button>
    </th>
  );
}

/**
 * The up/down chevron sort indicator. Faint (40% opacity) on hover for an
 * inactive sortable column; ``--brand``-accented for the active column
 * with the inactive direction dimmed (Appaloosa ``SortIndicator``,
 * Meridian tokens). Decorative — the ``aria-sort`` on the ``<th>`` is the
 * accessible signal.
 */
function SortIndicator({
  active,
  dir,
}: {
  active: boolean;
  dir: AuditSortDir | null;
}): React.ReactElement {
  if (!active) {
    return (
      <span
        aria-hidden="true"
        className="ml-0.5 inline-flex flex-col opacity-0 transition-opacity group-hover:opacity-40"
        data-testid="audit-sort-indicator-inactive"
      >
        <CaretUp size={8} />
        <CaretDown size={8} />
      </span>
    );
  }
  return (
    <span aria-hidden="true" className="ml-0.5 inline-flex flex-col text-brand">
      <CaretUp size={8} className={dir === "asc" ? "opacity-100" : "opacity-20"} />
      <CaretDown size={8} className={dir === "desc" ? "opacity-100" : "opacity-20"} />
    </span>
  );
}

interface AuditRowProps {
  item: AuditEventItem;
  columns: readonly AuditColumnDef[];
  expanded: boolean;
  onToggle: () => void;
}

/**
 * One audit-event row + its inline expansion.
 *
 * The summary ``<tr>`` is keyboard-activatable (Enter / Space) and
 * carries ``aria-expanded``; its cells are driven by the active
 * ``columns`` (cog-toggled visibility) via :data:`CELL_RENDERERS`.
 * Expanding renders a second ``<tr>`` with a full-width cell (colSpan =
 * visible columns + the expand column) showing the envelope fields + the
 * formatted payload (Flow J Frame 3). Outcome is color-coded per
 * :func:`outcomeMeta` (Success / Failure / Denied visually distinct).
 */
function AuditRow({ item, columns, expanded, onToggle }: AuditRowProps): React.ReactElement {
  return (
    <>
      <tr
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggle();
          }
        }}
        data-testid={`audit-row-${item.id}`}
        className="cursor-pointer border-b border-border hover:bg-bg-elevated focus:outline-none focus-visible:bg-bg-elevated"
      >
        <td className="px-2 py-2 align-top text-text-muted">
          {expanded ? (
            <CaretDown size={14} aria-hidden="true" />
          ) : (
            <CaretRight size={14} aria-hidden="true" />
          )}
        </td>
        {columns.map((col) => (
          <td
            key={col.key}
            className="whitespace-nowrap px-3 py-2 align-top"
            data-testid={`audit-cell-${col.key}-${item.id}`}
          >
            {CELL_RENDERERS[col.key]?.(item)}
          </td>
        ))}
      </tr>
      {expanded && (
        <tr
          className="border-b border-border bg-bg-surface"
          data-testid={`audit-row-detail-${item.id}`}
        >
          <td colSpan={columns.length + 1} className="px-4 py-3">
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-body-sm">
              <dt className="text-text-secondary">Actor kind</dt>
              <dd className="text-text-primary">{item.actor_kind ?? "—"}</dd>
              <dt className="text-text-secondary">Correlation ID</dt>
              <dd className="font-mono text-text-primary">{item.correlation_id ?? "—"}</dd>
              <dt className="text-text-secondary">Service</dt>
              <dd className="text-text-primary">{item.service}</dd>
              <dt className="text-text-secondary">Record ID</dt>
              <dd className="font-mono text-text-primary">{item.id}</dd>
            </dl>
            <p className="mt-2 text-meta uppercase tracking-wide text-text-secondary">Payload</p>
            <pre
              className="mt-1 max-h-64 overflow-auto rounded border border-border bg-bg-base p-2 font-mono text-meta text-text-secondary"
              data-testid={`audit-row-payload-${item.id}`}
            >
              {JSON.stringify(item.details, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

// ---- Render-state stubs ------------------------------------------

function LoadingState(): React.ReactElement {
  return (
    <div
      data-testid="audit-log-loading"
      role="status"
      aria-label="Loading audit log"
      className="flex h-full w-full items-center justify-center bg-bg-base text-text-secondary"
    >
      <p className="text-body-sm">Loading audit log…</p>
    </div>
  );
}

function EmptyState(): React.ReactElement {
  return (
    <div
      data-testid="audit-log-empty"
      role="region"
      aria-label="No audit records"
      className="flex h-full w-full flex-col items-center justify-center gap-2 bg-bg-base text-text-secondary"
    >
      <p className="text-body text-text-primary">No audit records match your filter</p>
      <p className="text-body-sm text-text-muted">
        Try widening the time range or clearing the actor / action filters.
      </p>
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <div
      data-testid="audit-log-error"
      role="alert"
      className="flex h-full w-full flex-col items-center justify-center gap-3 bg-bg-base"
    >
      <p className="text-body text-destructive">Could not load the audit log</p>
      <p className="px-6 text-center text-body-sm text-text-secondary">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded border border-border bg-bg-surface px-3 py-1 text-body-sm text-text-primary hover:bg-bg-elevated"
      >
        Retry
      </button>
    </div>
  );
}

function ForbiddenState(): React.ReactElement {
  return (
    <div
      data-testid="audit-log-forbidden"
      role="alert"
      className="flex h-full w-full flex-col items-center justify-center gap-2 bg-bg-base text-text-secondary"
    >
      <p className="text-body text-text-primary">Access restricted</p>
      <p className="px-6 text-center text-body-sm text-text-muted">
        The audit log is available to administrators only.
      </p>
    </div>
  );
}
